import { Box, Button, HStack, Spinner, Text } from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useListCoaches, useListLocations } from 'hooks/useApiQueries';
import { useEffect, useState } from 'react';
import { CancelVisitModal } from 'screens/Class/components/CancelVisitModal';
import { PickaspotClass } from 'types/class';
import { ClientVisit } from 'types/client';
import { isVisitLateCancel } from 'utils/functions';
import { HTTP } from 'utils/http';
import { getBrandColor } from 'utils/brandColor';

const VisitItem: React.FC<{ futureVisit: ClientVisit }> = ({ futureVisit }) => {
  const [isCancelled, setIsCancelled] = useState(false);

  const [loadingSpot, setLoadingSpot] = useState(false);
  const [memberSpot, setMemberSpot] = useState<number | null>(null);

  const { data: coaches } = useListCoaches();
  const getCoachById = (id: number) =>
    coaches?.find((coach) => {
      return coach.Id === id && futureVisit.SiteId === coach.SiteId;
    });
  const coach = getCoachById(futureVisit.StaffId);

  const { data: locations } = useListLocations();
  const getLocationById = (id: number) =>
    locations?.find((location) => location.Id === id && futureVisit.SiteId == location.SiteID);
  const location = getLocationById(futureVisit.LocationId);
  const primaryColor = getBrandColor();

  // Fonction de callback appelée après une annulation réussie
  const handleCancelSuccess = () => {
    setIsCancelled(true); // Mettre à jour l'état pour indiquer que la visite a été annulée plutôt que de la supprimer instantanément de la liste
  };

  useEffect(() => {
    if (futureVisit.AppointmentStatus === 'Cancelled') {
      setIsCancelled(true);
    }
  }, [futureVisit.AppointmentStatus]);

  const handleDiscoverSpot = async () => {
    setLoadingSpot(true);
    try {
      const response = await HTTP.get<PickaspotClass['reservations'][number]>(
        `/class/${futureVisit.ClassId}/reservation`,
        {
          SiteId: futureVisit.SiteId,
          VisitId: futureVisit.Id,
        },
      ).send();
      setMemberSpot(response?.spot?.spotNumber || null);
    } catch (error) {
      // Gérez l'erreur si nécessaire
    } finally {
      setLoadingSpot(false);
    }
  };

  return (
    <Box
      key={futureVisit.Id}
      w="full"
      className="accountVisit"
      sx={{ borderBottom: '1px solid black', paddingBottom: 3 }}
    >
      <Text textTransform={'uppercase'} fontSize="l" mt={3} fontWeight="semibold">
        {location?.Name}
      </Text>

      <HStack justifyContent={'space-between'} alignItems={'flex-start'}>
        <Box>
          <Text color={primaryColor} textTransform={'uppercase'} fontSize="l" fontWeight="semibold">
            {futureVisit.Name}
          </Text>
          <Text align="left" fontSize="xs">
            {format(parseISO(futureVisit.StartDateTime), 'eee dd/MM/yyyy - HH:mm', { locale: fr })} /{' '}
            {format(parseISO(futureVisit.EndDateTime), 'HH:mm')}
          </Text>
          <Box sx={{ display: 'flex' }}>
            <Text fontSize="xs" align="left">
              {coach?.FirstName} -
            </Text>
            {memberSpot ? (
              <Text fontSize="xs" align="left" marginLeft="3px">
                Spot : {memberSpot}
              </Text>
            ) : (
              <Button
                sx={{
                  padding: 0,
                  fontWeight: 400,
                  textDecoration: 'underline',
                  marginTop: '1px',
                  height: 'auto',
                  marginLeft: '3px',
                }}
                onClick={handleDiscoverSpot}
                variant="underlined"
                fontSize="xs"
                isLoading={loadingSpot}
                spinner={<Spinner size="xs" />}
              >
                Mon Spot
              </Button>
            )}
          </Box>
        </Box>
        <Box>
          {!isCancelled ? (
            <CancelVisitModal visit={futureVisit} onCancelSuccess={handleCancelSuccess}>
              <HStack alignContent={'center'} justifyContent={'center'}>
                <Button
                  textTransform="uppercase"
                  variant="contained"
                  size="xs"
                  sx={{ padding: '1em', display: 'block', height: 'auto', width: 'auto' }}
                >
                  {isVisitLateCancel(futureVisit) ? (
                    <Box>
                      <Text as="span">Annulation tardive</Text>
                    </Box>
                  ) : (
                    <Box>
                      <Text as="span">Annuler la séance</Text>
                    </Box>
                  )}
                </Button>
              </HStack>
            </CancelVisitModal>
          ) : (
            <Text
              align="center"
              width="101px"
              fontWeight="700"
              color="red.500"
              textTransform="uppercase"
              mt={4}
              fontSize="sm"
            >
              Annulée
            </Text>
          )}
        </Box>
      </HStack>
    </Box>
  );
};

export default VisitItem;
